import React from 'react';
import {
  Alert as MuiAlert,
  Card,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AlertDialog from '../../../../components/AlertDialog';
import { getCompletedComplements, postComplementInvite } from '../../../../queries/complementInvite';
import type {
  CompletedComplement,
  DeclaredComplementRelationship,
} from '../../../../types/complementInvite';
import ShareDialog from './ShareDialog';
import {
  LEAD_PICKER_PAGE,
  LEAD_SHARE_PAGE,
  leadResultCategory,
} from './analyticsPages';
import {
  CLAVE_UNICA_NOTICE,
  COMPLEMENT_DISCLOSURE,
  mintErrorCopy,
  OTHER_COMPLETED_TITLE,
  PICKER_TITLE,
  RELATIONSHIP_LABELS,
} from './copy';

type CandidateDialogProps = {
  open: boolean;
  token: string;
  onClose: () => void;
  pageCategory?: string;
};

const RELATIONSHIPS: DeclaredComplementRelationship[] = [
  'conyuge',
  'hijos_en_comun',
  'hijo',
  'padre',
  'otro',
];

export default function CandidateDialog({
  open, token, onClose, pageCategory = leadResultCategory(),
}: CandidateDialogProps) {
  const theme = useTheme();
  const [selected, setSelected] = React.useState<DeclaredComplementRelationship | ''>('');
  const [completed, setCompleted] = React.useState<CompletedComplement[]>([]);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setCompleted([]);
    getCompletedComplements(token)
      .then(({ data }) => setCompleted(data.completedComplements || []))
      .catch(() => setCompleted([]));
  }, [open, token]);
  const [invite, setInvite] = React.useState<{ url: string, expiresAt: string } | null>(null);
  const [mintError, setMintError] = React.useState<string | null>(null);

  const accentFor = (relationship: DeclaredComplementRelationship) => (
    relationship === 'conyuge' || relationship === 'hijos_en_comun' || relationship === 'hijo'
      ? theme.palette.secondary.main
      : theme.palette.primary.main
  );

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    setMintError(null);
    try {
      const { data } = await postComplementInvite({
        token,
        declaredRelationship: selected,
      });
      setInvite({ url: data.url, expiresAt: data.expiresAt });
    } catch (e) {
      setMintError(mintErrorCopy(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (invite) {
    return (
      <ShareDialog
        open
        url={invite.url}
        expiresAt={invite.expiresAt}
        pageCall={{ category: pageCategory, name: LEAD_SHARE_PAGE }}
        onClose={() => { setInvite(null); onClose(); }}
      />
    );
  }

  return (
    <AlertDialog
      isTriggered={open}
      onCloseAction={onClose}
      pageCall={{ category: pageCategory, name: LEAD_PICKER_PAGE }}
      maxWidth="xs"
      dialogTitle={(
        <Typography variant="subtitle2" fontWeight="bold" align="center" component="div">
          {PICKER_TITLE}
        </Typography>
      )}
      submitButtonLabel="Continuar"
      submitButtonDisabled={submitting || !selected}
      action={submit}
      closeAfterSubmit={false}
      dialogContentStyle={{ paddingTop: 8, paddingBottom: 8 }}
      dialogContent={(
        <>
          <RadioGroup
            value={selected}
            onChange={(event) => {
              setMintError(null);
              setSelected(event.target.value as DeclaredComplementRelationship);
            }}
          >
            {RELATIONSHIPS.map((relationship) => {
              const isSelected = selected === relationship;
              const accent = accentFor(relationship);
              return (
                <Card
                  key={relationship}
                  variant="outlined"
                  style={{
                    marginBottom: 10,
                    padding: '6px 10px',
                    borderRadius: 12,
                    cursor: 'pointer',
                    border: isSelected
                      ? `1.5px solid ${accent}`
                      : `1px solid ${alpha(accent, 0.35)}`,
                    backgroundColor: isSelected
                      ? alpha(accent, 0.12)
                      : alpha(accent, 0.05),
                    boxShadow: isSelected
                      ? `0 0 0 3px ${alpha(accent, 0.16)}`
                      : 'none',
                  }}
                >
                  <FormControlLabel
                    value={relationship}
                    control={<Radio size="small" style={{ color: accent }} />}
                    label={(
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        component="span"
                        style={{ fontSize: 13 }}
                      >
                        {RELATIONSHIP_LABELS[relationship]}
                      </Typography>
                    )}
                    style={{ width: '100%', margin: 0 }}
                  />
                </Card>
              );
            })}
          </RadioGroup>
          {completed.length > 0 && (
            <div style={{ marginTop: 12, textAlign: 'left' }}>
              <Typography
                variant="caption"
                component="div"
                style={{ fontSize: 11, marginBottom: 8, fontWeight: 700 }}
              >
                {OTHER_COMPLETED_TITLE}
              </Typography>
              {completed.map((person) => (
                <Card
                  key={`${person.relationship}-${person.displayName}`}
                  variant="outlined"
                  style={{
                    marginBottom: 8,
                    padding: '8px 12px',
                    borderRadius: 10,
                    opacity: 0.7,
                  }}
                >
                  <Typography variant="caption" fontWeight="bold" component="div">
                    {person.displayName}
                  </Typography>
                  <Typography variant="caption" component="div" style={{ fontSize: 11 }}>
                    {RELATIONSHIP_LABELS[person.relationship]}
                  </Typography>
                </Card>
              ))}
            </div>
          )}
          {mintError && (
            <Typography
              variant="caption"
              component="div"
              align="center"
              style={{
                marginTop: 12,
                fontSize: 12,
                color: theme.palette.error.main,
              }}
            >
              {mintError}
            </Typography>
          )}
          <MuiAlert
            severity="info"
            style={{ marginTop: 12, padding: '4px 10px' }}
          >
            <Typography
              variant="caption"
              component="div"
              style={{ fontSize: 11, lineHeight: 1.4, color: 'white' }}
            >
              {CLAVE_UNICA_NOTICE}
            </Typography>
          </MuiAlert>
          <Typography
            variant="caption"
            component="div"
            style={{ fontSize: 11, lineHeight: 1.4, marginTop: 12 }}
          >
            {COMPLEMENT_DISCLOSURE}
          </Typography>
        </>
      )}
    />
  );
}

CandidateDialog.defaultProps = {
  pageCategory: leadResultCategory(),
};
